import { useState, useMemo, useCallback, useEffect } from "react";
import { useLayout } from "@/hooks/use-layout";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useErrorDialog } from "@/hooks/use-error-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import {
  FileText,
  Send,
  Eye,
  Shield,
  Users,
  Calendar,
  ClipboardList,
  AlertTriangle,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import type {
  GovernanceActiveResponse,
  LanesResponse,
  ActorsResponse,
  ReferenceListsResponse,
  WorkflowRulesResponse,
  AuthorityLaneRecord,
  AuthorityActorRecord,
  ReferenceListItemRecord,
  WorkflowRuleRecord,
} from "@shared/v2/types";

interface FormData {
  authority_lane_id: string;
  authority_lane_key: string;
  origin_reference: string;
  meeting_name: string;
  meeting_type: string;
  meeting_date: string;
  title: string;
  instruction: string;
  doc_type: string;
  assigned_to: string;
  current_responsible: string;
  team_function: string;
  due_date: string;
  priority: string;
  review_required: boolean;
  skip_review_next_handling: string;
  signoff_required: boolean;
  signoff_actor_key: string;
  on_behalf_of: string;
}

const INITIAL_FORM: FormData = {
  authority_lane_id: "",
  authority_lane_key: "",
  origin_reference: "",
  meeting_name: "",
  meeting_type: "",
  meeting_date: "",
  title: "",
  instruction: "",
  doc_type: "",
  assigned_to: "",
  current_responsible: "",
  team_function: "",
  due_date: "",
  priority: "",
  review_required: true,
  skip_review_next_handling: "",
  signoff_required: true,
  signoff_actor_key: "",
  on_behalf_of: "",
};

function NotAuthorizedToRegisterDialog({ onClose }: { onClose: () => void }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0d1b3e] border-white/10 text-white" data-testid="dialog-not-authorized-register">
        <DialogHeader>
          <DialogTitle>Not authorized to register tasks</DialogTitle>
          <DialogDescription className="text-white/70 pt-2">
            You are not authorized to register tasks. Please request permission from your Chief.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button onClick={onClose} data-testid="button-not-authorized-back">Back to Updates</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ActionItemRegisterPage() {
  const { containerClass } = useLayout();
  const { canRegisterTasks, isLoading: authLoading } = useAuth();
  const [, setLocation] = useLocation();
  const [form, setForm] = useState<FormData>({ ...INITIAL_FORM });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [submittedId, setSubmittedId] = useState<string | null>(null);
  const [autoRefGenerated, setAutoRefGenerated] = useState(false);
  const [isGeneratingRef, setIsGeneratingRef] = useState(false);
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();

  const governanceQuery = useQuery<GovernanceActiveResponse>({
    queryKey: ["/api/governance/active"],
  });

  const lanesQuery = useQuery<LanesResponse>({
    queryKey: ["/api/governance/lanes"],
  });

  const actorsQuery = useQuery<ActorsResponse>({
    queryKey: ["/api/governance/actors"],
  });

  const refListsQuery = useQuery<ReferenceListsResponse>({
    queryKey: ["/api/governance/reference-lists"],
  });

  const workflowRulesQuery = useQuery<WorkflowRulesResponse>({
    queryKey: ["/api/governance/workflow-rules"],
  });

  const peopleQuery = useQuery<{ team_members: { name: string; team_function: string | null }[]; authority_lanes: { lane_key: string; display_name: string }[]; combined: string[]; combined_meta?: { name: string; is_workflow_actor: boolean }[] }>({
    queryKey: ["/api/people"],
  });

  const principalsQuery = useQuery<{ data: { name: string; can_signoff: boolean }[] }>({
    queryKey: ["/api/my-ea-principals"],
  });
  const eaPrincipals = principalsQuery.data?.data ?? [];

  const isLoading =
    governanceQuery.isLoading ||
    lanesQuery.isLoading ||
    actorsQuery.isLoading ||
    refListsQuery.isLoading ||
    workflowRulesQuery.isLoading ||
    peopleQuery.isLoading;

  const hasError =
    governanceQuery.isError ||
    lanesQuery.isError ||
    actorsQuery.isError ||
    refListsQuery.isError ||
    workflowRulesQuery.isError;

  const lanes = lanesQuery.data?.data ?? [];
  const actors = actorsQuery.data?.data ?? [];
  const refLists = refListsQuery.data?.data ?? {};
  const rules = workflowRulesQuery.data?.data ?? [];
  const governanceVersion = governanceQuery.data?.data;

  const meetingTypes = refLists["MEETING_TYPE"] ?? [];
  const docTypes = refLists["DOC_TYPE"] ?? [];
  const priorities = refLists["PRIORITY"] ?? [];
  const teamMembers = peopleQuery.data?.team_members ?? [];
  const combinedPeople = peopleQuery.data?.combined ?? [];
  // Ceremonial workflow actors (SDF MD, SDF PMO, SDF Board Secretary,
  // governance lane display names) are NOT valid direct assignees — they
  // are queue/destination identities reached through dedicated workflow
  // transitions (e.g. "Send for MD Review"), not the generic Currently
  // With picker. The backend already rejects writes targeting them
  // (see getCeremonialActorNameSet guards in server/v2/routes.ts);
  // this filter keeps the UI consistent so the option is never offered
  // in the first place.
  const combinedMeta = peopleQuery.data?.combined_meta ?? combinedPeople.map(name => ({ name, is_workflow_actor: false }));
  const assignablePeople = combinedMeta.filter(m => !m.is_workflow_actor).map(m => m.name);

  const signoffActors = useMemo(() => {
    if (!form.authority_lane_key) return [];
    return actors.filter(
      (a) => a.lane_key === form.authority_lane_key && a.can_signoff && a.is_active
    );
  }, [actors, form.authority_lane_key]);

  const defaultReviewRequired = useMemo(() => {
    const rule = rules.find((r) => r.rule_key === "REVIEW_REQUIRED" && !r.scope_lane_key);
    if (rule && typeof rule.value_json === "object" && rule.value_json !== null) {
      return (rule.value_json as { default?: boolean }).default ?? true;
    }
    return true;
  }, [rules]);

  const canSkipReview = useMemo(() => {
    if (!form.meeting_type) return false;
    const rule = rules.find(
      (r) => r.rule_key === "SKIP_REVIEW_ALLOWED" && (r.scope_origin_type === form.meeting_type || (!r.scope_origin_type && !r.scope_lane_key))
    );
    if (rule && typeof rule.value_json === "object" && rule.value_json !== null) {
      return (rule.value_json as { allowed?: boolean }).allowed ?? false;
    }
    return false;
  }, [rules, form.meeting_type]);

  const defaultSignoffActor = useMemo(() => {
    if (!form.authority_lane_key) return "";
    const rule = rules.find(
      (r) => r.rule_key === "SIGNOFF_AUTHORITY" && r.scope_lane_key === form.authority_lane_key
    );
    if (rule && typeof rule.value_json === "object" && rule.value_json !== null) {
      return (rule.value_json as { required_actor?: string }).required_actor ?? "";
    }
    return "";
  }, [rules, form.authority_lane_key]);

  function setField<K extends keyof FormData>(key: K, value: FormData[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleLaneChange(laneId: string) {
    const lane = lanes.find((l) => l.id === laneId);
    const laneKey = lane?.lane_key ?? "";
    const signoffRule = rules.find(
      (r) => r.rule_key === "SIGNOFF_AUTHORITY" && r.scope_lane_key === laneKey
    );
    const autoActor =
      signoffRule &&
      typeof signoffRule.value_json === "object" &&
      signoffRule.value_json !== null
        ? (signoffRule.value_json as { required_actor?: string }).required_actor ?? ""
        : "";
    setForm((prev) => ({
      ...prev,
      authority_lane_id: laneId,
      authority_lane_key: laneKey,
      signoff_actor_key: autoActor,
      meeting_type: "",
      origin_reference: "",
    }));
    setAutoRefGenerated(false);
  }

  const generateAutoRef = useCallback(async (meetingTypeKey: string, meetingDate: string) => {
    if (!meetingTypeKey || !meetingDate) {
      setAutoRefGenerated(false);
      return;
    }
    const mtItem = meetingTypes.find((m) => m.item_key === meetingTypeKey);
    if (!mtItem?.ref_code) {
      setAutoRefGenerated(false);
      return;
    }

    setIsGeneratingRef(true);
    try {
      const res = await apiRequest("POST", "/api/ref-sequence/next", {
        meetingTypeCode: mtItem.ref_code,
        meetingDate,
      });
      const data = await res.json();
      if (data?.data?.refNumber) {
        setForm((prev) => ({ ...prev, origin_reference: data.data.refNumber }));
        setAutoRefGenerated(true);
      }
    } catch {
      setAutoRefGenerated(false);
    } finally {
      setIsGeneratingRef(false);
    }
  }, [meetingTypes]);

  function handleMeetingTypeChange(val: string) {
    setField("meeting_type", val);
    if (form.meeting_date) {
      generateAutoRef(val, form.meeting_date);
    }
  }

  function handleMeetingDateChange(val: string) {
    setField("meeting_date", val);
    if (form.meeting_type) {
      generateAutoRef(form.meeting_type, val);
    }
  }

  function buildPayload() {
    const lane = lanes.find((l) => l.id === form.authority_lane_id);
    return {
      governance_version_id: governanceVersion?.id ?? null,
      authority: {
        lane_id: form.authority_lane_id || null,
        lane_key: lane?.lane_key ?? null,
        lane_display: lane?.display_name ?? null,
      },
      opportunity: {
        name: form.meeting_name.trim() || null,
      },
      origin: {
        meeting_type: form.meeting_type || null,
        reference: form.origin_reference.trim() || null,
        meeting_date: form.meeting_date || null,
      },
      instruction: {
        title: form.title || null,
        verbatim: form.instruction || null,
      },
      expected_output: {
        doc_type: form.doc_type || null,
      },
      accountability: {
        assigned_to: form.assigned_to || null,
        team_function: form.team_function || null,
        due_date: form.due_date || null,
        priority: form.priority || null,
      },
      review_path: {
        review_required: form.review_required,
        skip_review_next_handling: !form.review_required
          ? form.skip_review_next_handling || null
          : null,
      },
      signoff: {
        signoff_required: form.signoff_required,
        signoff_actor_key: form.signoff_required ? form.signoff_actor_key || null : null,
        signoff_actor_display: form.signoff_required
          ? actors.find((a) => a.actor_key === form.signoff_actor_key)?.display_name ?? null
          : null,
      },
      _meta: {
        generated_at: new Date().toISOString(),
        status: "DRAFT_PREVIEW",
      },
    };
  }

  function buildApiPayload() {
    return {
      governanceVersionId: governanceVersion?.id ?? "",
      authorityLane: form.authority_lane_key,
      status: "REGISTERED",
      title: form.title.trim(),
      instruction: form.instruction.trim(),
      originType: form.meeting_type || undefined,
      originReference: form.origin_reference.trim() || undefined,
      meetingName: form.meeting_name.trim() || undefined,
      meetingType: form.meeting_type || undefined,
      meetingDate: form.meeting_date || undefined,
      docType: form.doc_type || undefined,
      assignedTo: form.assigned_to.trim(),
      currentResponsible: form.current_responsible || undefined,
      teamFunction: form.team_function.trim() || undefined,
      dueDate: form.due_date,
      priority: form.priority,
      reviewRequired: form.review_required,
      skipReviewNextHandling: !form.review_required ? form.skip_review_next_handling || undefined : undefined,
      signoffRequired: form.signoff_required,
      signoffActorKey: form.signoff_required ? form.signoff_actor_key || undefined : undefined,
      onBehalfOf: form.on_behalf_of || undefined,
    };
  }

  const submitMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/action-items", buildApiPayload());
      return res.json();
    },
    onSuccess: (data: { data: { id: string } }) => {
      setSubmittedId(data.data.id);
      queryClient.invalidateQueries({ queryKey: ["/api/action-items"] });
      toast({
        title: "Action item registered",
        description: `ID: ${data.data.id}`,
      });
    },
    onError: (err: Error) => {
      showFromApiError("Registration Failed", err);
    },
  });

  function handleSubmit() {
    submitMutation.mutate();
  }

  function handleReset() {
    setForm({ ...INITIAL_FORM });
    setSubmittedId(null);
    setPreviewOpen(false);
  }

  const isFormValid =
    form.authority_lane_id &&
    form.meeting_type &&
    form.title.trim() &&
    form.instruction.trim() &&
    form.assigned_to.trim() &&
    form.due_date &&
    form.priority &&
    (!form.signoff_required || form.signoff_actor_key);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(135deg, #0d1b3e 0%, #1a2a52 50%, #0f1d40 100%)" }}>
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-[#FF9F43]" />
          <span className="text-white/70 text-sm">Loading governance configuration...</span>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(135deg, #0d1b3e 0%, #1a2a52 50%, #0f1d40 100%)" }}>
        <Card className="border-red-500/30 bg-red-500/10 max-w-md">
          <CardContent className="p-6 flex flex-col items-center gap-3">
            <AlertTriangle className="h-8 w-8 text-red-400" />
            <p className="text-white text-center">
              Failed to load governance configuration. Please try again.
            </p>
            <Button variant="outline" onClick={() => window.location.reload()} data-testid="button-retry">
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!authLoading && !canRegisterTasks) {
    return <NotAuthorizedToRegisterDialog onClose={() => setLocation("/updates")} />;
  }

  return (
    <div
      className="min-h-screen"
      style={{
        background: "linear-gradient(135deg, #0d1b3e 0%, #1a2a52 50%, #0f1d40 100%)",
      }}
    >
      <div className={`${containerClass} mx-auto px-6 py-8`}>
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div
              className="h-10 w-10 rounded-md flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #FF9F43, #FF6B35)" }}
            >
              <ClipboardList className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-white" data-testid="text-page-title">
                Register Action Item
              </h1>
              <p className="text-white/50 text-sm">
                Governance v{governanceVersion?.name ?? "—"}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-6">
          <Card
            className="border-white/10"
            style={{ background: "rgba(255,255,255,0.06)" }}
          >
            <CardHeader className="pb-4">
              <CardTitle className="text-base font-medium text-white flex items-center gap-2">
                <Shield className="h-4 w-4 text-[#FF9F43]" />
                Authority & Origin
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label className="text-white/70 text-sm">Authority Lane</Label>
                  <Select
                    value={form.authority_lane_id}
                    onValueChange={handleLaneChange}
                  >
                    <SelectTrigger data-testid="select-authority-lane" className="bg-white/5 border-white/10 text-white">
                      <SelectValue placeholder="Select authority lane" />
                    </SelectTrigger>
                    <SelectContent>
                      {lanes
                        .filter((l) => l.is_active)
                        .sort((a, b) => a.sort_order - b.sort_order)
                        .map((lane) => (
                          <SelectItem key={lane.id} value={lane.id} data-testid={`option-lane-${lane.lane_key}`}>
                            {lane.display_name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label className="text-white/70 text-sm">Meeting Type</Label>
                  <Select
                    value={form.meeting_type}
                    onValueChange={handleMeetingTypeChange}
                  >
                    <SelectTrigger data-testid="select-meeting-type-origin" className="bg-white/5 border-white/10 text-white">
                      <SelectValue placeholder="Select meeting type" />
                    </SelectTrigger>
                    <SelectContent>
                      {meetingTypes
                        .filter((m) => m.is_active)
                        .sort((a, b) => a.sort_order - b.sort_order)
                        .map((item) => (
                          <SelectItem key={item.item_key} value={item.item_key} data-testid={`option-meeting-${item.item_key}`}>
                            {item.label}
                            {item.ref_code && <span className="text-white/30 ml-1 text-xs">({item.ref_code})</span>}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label className="text-white/70 text-sm">
                  Reference Number
                  {autoRefGenerated && <span className="text-[#28C76F] text-xs ml-2">(auto-generated)</span>}
                  {isGeneratingRef && <span className="text-cyan-400 text-xs ml-2">generating...</span>}
                </Label>
                <Input
                  value={form.origin_reference}
                  onChange={(e) => {
                    if (!autoRefGenerated) setField("origin_reference", e.target.value);
                  }}
                  readOnly={autoRefGenerated}
                  placeholder="Select meeting type & date to auto-generate"
                  className={`bg-white/5 border-white/10 text-white placeholder:text-white/30 ${autoRefGenerated ? "font-mono text-[#28C76F] bg-[#28C76F]/5" : ""}`}
                  data-testid="input-origin-reference"
                />
                <p className="text-white/30 text-xs">
                  {autoRefGenerated
                    ? "This reference was automatically generated from the meeting type and date"
                    : "Select a meeting type and date to auto-generate, or enter manually"}
                </p>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label className="text-white/70 text-sm">Meeting Name</Label>
                <Input
                  value={form.meeting_name}
                  onChange={(e) => setField("meeting_name", e.target.value)}
                  placeholder="e.g. Q2 Board Meeting, Investment Committee, Audit Review"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="input-meeting-name"
                />
                <p className="text-white/30 text-xs">Optional — the meeting this action item relates to</p>
              </div>

              <div className="pt-2 border-t border-white/5">
                <div className="flex flex-col gap-1.5">
                  <Label className="text-white/70 text-sm">Meeting Date</Label>
                  <Input
                    type="date"
                    value={form.meeting_date}
                    onChange={(e) => handleMeetingDateChange(e.target.value)}
                    className="bg-white/5 border-white/10 text-white md:max-w-[50%]"
                    data-testid="input-meeting-date"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card
            className="border-white/10"
            style={{ background: "rgba(255,255,255,0.06)" }}
          >
            <CardHeader className="pb-4">
              <CardTitle className="text-base font-medium text-white flex items-center gap-2">
                <FileText className="h-4 w-4 text-[#00CFE8]" />
                Instruction
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <Label className="text-white/70 text-sm">Title</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setField("title", e.target.value)}
                  placeholder="Brief descriptive title for this action item"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="input-title"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label className="text-white/70 text-sm">Instruction (Verbatim)</Label>
                <Textarea
                  value={form.instruction}
                  onChange={(e) => setField("instruction", e.target.value)}
                  placeholder="Capture the exact wording of the instruction or directive..."
                  rows={4}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 resize-none"
                  data-testid="input-instruction"
                />
              </div>
            </CardContent>
          </Card>

          <Card
            className="border-white/10"
            style={{ background: "rgba(255,255,255,0.06)" }}
          >
            <CardHeader className="pb-4">
              <CardTitle className="text-base font-medium text-white flex items-center gap-2">
                <Send className="h-4 w-4 text-[#28C76F]" />
                Expected Outputs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-1.5">
                <Label className="text-white/70 text-sm">Document Type</Label>
                <Select
                  value={form.doc_type}
                  onValueChange={(val) => setField("doc_type", val)}
                >
                  <SelectTrigger data-testid="select-doc-type" className="bg-white/5 border-white/10 text-white">
                    <SelectValue placeholder="Select document type" />
                  </SelectTrigger>
                  <SelectContent>
                    {docTypes
                      .filter((d) => d.is_active)
                      .sort((a, b) => a.sort_order - b.sort_order)
                      .map((item) => (
                        <SelectItem key={item.item_key} value={item.item_key} data-testid={`option-doc-${item.item_key}`}>
                          {item.label}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card
            className="border-white/10"
            style={{ background: "rgba(255,255,255,0.06)" }}
          >
            <CardHeader className="pb-4">
              <CardTitle className="text-base font-medium text-white flex items-center gap-2">
                <Users className="h-4 w-4 text-[#7367F0]" />
                Accountability
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label className="text-white/70 text-sm">Assigned To</Label>
                  <Select
                    value={form.assigned_to}
                    onValueChange={(val) => {
                      setField("assigned_to", val);
                      const member = teamMembers.find(m => m.name === val);
                      if (member?.team_function) {
                        setField("team_function", member.team_function);
                      }
                    }}
                  >
                    <SelectTrigger data-testid="select-assigned-to" className="bg-white/5 border-white/10 text-white">
                      <SelectValue placeholder="Select team member" />
                    </SelectTrigger>
                    <SelectContent>
                      {teamMembers.map((m) => (
                        <SelectItem key={m.name} value={m.name} data-testid={`option-assigned-${m.name}`}>
                          {m.name}{m.team_function ? ` (${m.team_function})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label className="text-white/70 text-sm">Team / Function</Label>
                  <Input
                    value={form.team_function}
                    onChange={(e) => setField("team_function", e.target.value)}
                    placeholder="e.g. Legal, Finance, Operations"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    data-testid="input-team-function"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label className="text-white/70 text-sm">Currently With</Label>
                <Select
                  value={form.current_responsible}
                  onValueChange={(val) => setField("current_responsible", val)}
                >
                  <SelectTrigger data-testid="select-current-responsible" className="bg-white/5 border-white/10 text-white">
                    <SelectValue placeholder="Who has the ball right now?" />
                  </SelectTrigger>
                  <SelectContent>
                    {assignablePeople.map((name) => (
                      <SelectItem key={name} value={name} data-testid={`option-responsible-${name}`}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-white/30 text-xs">The person currently responsible for acting on this item. Workflow destinations like the SDF MD review queue are reached via dedicated transitions (e.g. "Send for MD Review"), not direct assignment.</p>
              </div>

              {eaPrincipals.length > 0 && (
                <div className="flex flex-col gap-1.5">
                  <Label className="text-white/70 text-sm">On behalf of</Label>
                  <Select
                    value={form.on_behalf_of || "__none__"}
                    onValueChange={(val) => setField("on_behalf_of", val === "__none__" ? "" : val)}
                  >
                    <SelectTrigger data-testid="select-on-behalf-of" className="bg-white/5 border-white/10 text-white">
                      <SelectValue placeholder="Acting as yourself" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__" data-testid="option-on-behalf-none">Acting as yourself</SelectItem>
                      {eaPrincipals.map((p) => (
                        <SelectItem key={p.name} value={p.name} data-testid={`option-on-behalf-${p.name}`}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-white/30 text-xs">Register this item on behalf of a principal you assist. The action is recorded under your name, attributed to them.</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label className="text-white/70 text-sm">Due Date</Label>
                  <div className="relative">
                    <Input
                      type="date"
                      value={form.due_date}
                      onChange={(e) => setField("due_date", e.target.value)}
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="input-due-date"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label className="text-white/70 text-sm">Priority</Label>
                  <Select
                    value={form.priority}
                    onValueChange={(val) => setField("priority", val)}
                  >
                    <SelectTrigger data-testid="select-priority" className="bg-white/5 border-white/10 text-white">
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent>
                      {priorities
                        .filter((p) => p.is_active)
                        .sort((a, b) => a.sort_order - b.sort_order)
                        .map((item) => (
                          <SelectItem key={item.item_key} value={item.item_key} data-testid={`option-priority-${item.item_key}`}>
                            {item.label}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card
            className="border-white/10"
            style={{ background: "rgba(255,255,255,0.06)" }}
          >
            <CardHeader className="pb-4">
              <CardTitle className="text-base font-medium text-white flex items-center gap-2">
                <Eye className="h-4 w-4 text-[#00CFE8]" />
                Review Path
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div className="flex flex-col gap-0.5">
                  <Label className="text-white/80 text-sm">EDGE MD review required</Label>
                  <span className="text-white/40 text-xs">
                    Default: {defaultReviewRequired ? "Required" : "Not required"}
                    {canSkipReview && " (skip allowed for this origin type)"}
                  </span>
                </div>
                <Switch
                  checked={form.review_required}
                  onCheckedChange={(val) => setField("review_required", val)}
                  disabled={!canSkipReview && defaultReviewRequired}
                  data-testid="switch-review-required"
                />
              </div>

              {!form.review_required && (
                <div className="flex flex-col gap-1.5 pt-2 border-t border-white/5">
                  <Label className="text-white/70 text-sm">
                    If review skipped, next handling
                  </Label>
                  <Select
                    value={form.skip_review_next_handling}
                    onValueChange={(val) => setField("skip_review_next_handling", val)}
                  >
                    <SelectTrigger data-testid="select-skip-handling" className="bg-white/5 border-white/10 text-white">
                      <SelectValue placeholder="Select next handling" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DIRECT_TO_SIGNOFF">Direct to Sign-off</SelectItem>
                      <SelectItem value="DIRECT_TO_OWNER">Direct to Owner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          <Card
            className="border-white/10"
            style={{ background: "rgba(255,255,255,0.06)" }}
          >
            <CardHeader className="pb-4">
              <CardTitle className="text-base font-medium text-white flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#28C76F]" />
                Sign-off
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div className="flex flex-col gap-0.5">
                  <Label className="text-white/80 text-sm">Sign-off required</Label>
                  {defaultSignoffActor && (
                    <span className="text-white/40 text-xs">
                      Default authority:{" "}
                      {actors.find((a) => a.actor_key === defaultSignoffActor)?.display_name ??
                        defaultSignoffActor}
                    </span>
                  )}
                </div>
                <Switch
                  checked={form.signoff_required}
                  onCheckedChange={(val) => setField("signoff_required", val)}
                  data-testid="switch-signoff-required"
                />
              </div>

              {form.signoff_required && (
                <div className="flex flex-col gap-1.5 pt-2 border-t border-white/5">
                  <Label className="text-white/70 text-sm">Sign-off by</Label>
                  <Select
                    value={form.signoff_actor_key}
                    onValueChange={(val) => setField("signoff_actor_key", val)}
                  >
                    <SelectTrigger data-testid="select-signoff-actor" className="bg-white/5 border-white/10 text-white">
                      <SelectValue placeholder={form.authority_lane_key ? "Select sign-off authority" : "Select authority lane first"} />
                    </SelectTrigger>
                    <SelectContent>
                      {signoffActors.map((actor) => (
                        <SelectItem
                          key={actor.actor_key}
                          value={actor.actor_key}
                          data-testid={`option-signoff-${actor.actor_key}`}
                        >
                          {actor.display_name}
                        </SelectItem>
                      ))}
                      {signoffActors.length === 0 && (
                        <SelectItem value="__none__" disabled>
                          No sign-off actors for selected lane
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          <Separator className="bg-white/10" />

          {submittedId ? (
            <Card className="border-[#28C76F]/30" style={{ background: "rgba(40,199,111,0.08)" }}>
              <CardContent className="p-6 flex flex-col items-center gap-4">
                <CheckCircle2 className="h-10 w-10 text-[#28C76F]" />
                <div className="text-center">
                  <p className="text-white font-medium mb-1" data-testid="text-success-title">
                    Action Item Registered
                  </p>
                  <p className="text-white/60 text-sm font-mono" data-testid="text-success-id">
                    {submittedId}
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="gap-2"
                  data-testid="button-register-another"
                >
                  <ClipboardList className="h-4 w-4" />
                  Register Another
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <Button
                variant="outline"
                onClick={() => setPreviewOpen(true)}
                disabled={!isFormValid}
                className="gap-2"
                data-testid="button-preview-payload"
              >
                <Eye className="h-4 w-4" />
                Preview JSON
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!isFormValid || submitMutation.isPending}
                className="gap-2"
                style={{
                  background: isFormValid && !submitMutation.isPending
                    ? "linear-gradient(135deg, #FF9F43, #FF6B35)"
                    : undefined,
                }}
                data-testid="button-submit"
              >
                {submitMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {submitMutation.isPending ? "Submitting..." : "Register Action Item"}
              </Button>
            </div>
          )}
        </div>
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-[#1a2a52] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#FF9F43]" />
              Payload Preview
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2">
            <Badge variant="outline" className="text-[#FF9F43] border-[#FF9F43]/30 mb-4">
              DRAFT — NOT SUBMITTED
            </Badge>
            <pre
              className="bg-black/30 rounded-md p-4 text-sm text-white/90 font-mono overflow-x-auto whitespace-pre-wrap"
              data-testid="text-payload-preview"
            >
              {JSON.stringify(buildPayload(), null, 2)}
            </pre>
          </div>
          <DialogFooter className="gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(JSON.stringify(buildPayload(), null, 2));
              }}
              className="gap-2"
              data-testid="button-copy-payload"
            >
              Copy to Clipboard
            </Button>
            <Button
              variant="outline"
              onClick={() => setPreviewOpen(false)}
              data-testid="button-close-preview"
            >
              Close
            </Button>
            <Button
              onClick={() => {
                setPreviewOpen(false);
                handleSubmit();
              }}
              disabled={submitMutation.isPending}
              className="gap-2"
              style={{ background: "linear-gradient(135deg, #FF9F43, #FF6B35)" }}
              data-testid="button-submit-from-preview"
            >
              <Send className="h-4 w-4" />
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
